import API from "../api/api";
import { useNavigate } from "react-router-dom";

/**
 * Authentication Service
 * ----------------------
 * The AuthenticationService consists of helpers to support various tasks namely:
 * - Login
 * - Logout
 * - Token Management (save, get, decode JWT token)
 * - Email Management (save, get)
 **/

export interface Login {
  email: string;
  password: string;
}

const API_BASE_URL = API.defaults.baseURL;

export class AuthenticationService {
  private static TOKEN_KEY = "auth-token";
  private static USER_EMAIL_KEY = "user-email";
  private static USER_ROLE_KEY = "user-role";
  private navigate = useNavigate();

  /*---------- LOGIN & LOGOUT ----------*/
  async login(login: Login): Promise<any> {
    return API.post(`${API_BASE_URL}/distributor/user/login`, login)
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        throw error;
      });
  }

  logout(): void {
    localStorage.clear();
    this.navigate("/login");
  }

  /*---------- TOKEN HELPERS ----------*/
  saveToken(token: string): void {
    localStorage.removeItem(AuthenticationService.TOKEN_KEY);
    localStorage.setItem(AuthenticationService.TOKEN_KEY, token);
  }

  static getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  static async getTokenForNewAPI(): Promise<string | null> {
    const response = await API.get("/Token");
    return response.data.token;
  }

  static getPropertyFromJtwToken(property: string) {
    const token = this.getToken();
    if (!token) return "";

    try {
      const decodedJWT = JSON.parse(atob(token.split(".")[1]));
      return decodedJWT[property] || "";
    } catch (error) {
      console.error("Error decoding JWT:", error);
      return "";
    }
  }

  /*---------- EMAIL HELPERS ----------*/
  saveEmail(email: string): void {
    localStorage.setItem(AuthenticationService.USER_EMAIL_KEY, email);
  }

  static getEmail(): string {
    const email = localStorage.getItem(this.USER_EMAIL_KEY);
    if (email) {
      return email;
    } else {
      return this.getPropertyFromJtwToken("email");
    }
  }

  /*---------- USER ROLE HELPERS ----------*/
  saveUserRole(role: string): void {
    localStorage.setItem(AuthenticationService.USER_ROLE_KEY, role);
  }

  static getUserRole(): string {
    const role = localStorage.getItem(this.USER_ROLE_KEY);
    if (role) {
      return role;
    } else {
      return this.getPropertyFromJtwToken(
        "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
      )[1];
    }
  }
}
